import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { InformationIcon } from '@repo/ui/icons'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@repo/ui/tooltip'
import { PropsWithChildren, ReactNode, useMemo, useState } from 'react'

type Props = {
  variant?: 'default' | 'secondary' | 'warning' | 'primary'
  size?: 'small' | 'normal'
  align?: 'center' | 'end' | 'start'
  icon?: ReactNode
  disabled?: boolean
  triggerClassName?: string
  contentVariant?: 'default' | 'secondary'
  contentClassName?: string
}

const InformationToolTip = ({
  children,
  icon,
  variant = 'default',
  size = 'normal',
  disabled = false,
  align = 'center',
  contentVariant = 'default',
  triggerClassName,
  contentClassName
}: PropsWithChildren<Props>) => {
  // We added external state because on mobile tooltip doesn't trigger properly
  const [open, setOpen] = useState(false)

  const informationIconColor = useMemo(() => {
    switch (variant) {
      case 'secondary':
        return tailwindColors.grey.DEFAULT
      case 'warning':
        return tailwindColors.orange.DEFAULT
      case 'primary':
        return tailwindColors.primary.DEFAULT
      default:
        return tailwindColors.fushia.DEFAULT
    }
  }, [])

  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip open={open} onOpenChange={setOpen}>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault()
              setOpen((prev) => !prev)
            }}
            className={cn('inline-flex shrink-0', triggerClassName, {
              'rounded-full bg-fushia-light': !icon,
              'bg-white-dark': !icon && variant === 'secondary',
              'bg-primary-light': !icon && variant === 'primary',
              'bg-orange-light': !icon && variant === 'warning'
            })}
          >
            {icon ?? (
              <InformationIcon
                height={size === 'small' ? '20' : '25'}
                width={size === 'small' ? '20' : '25'}
                color={informationIconColor}
              />
            )}
          </button>
        </TooltipTrigger>
        <TooltipContent
          sideOffset={8}
          align={align}
          className={cn(
            'mobile:max-w-xs h-full max-h-48 overflow-y-auto p-3 bg-grey rounded-lg [&::-webkit-scrollbar-thumb]:bg-[#ffffff90]',
            {
              hidden: disabled,
              'text-sm': size === 'small',
              'text-base': size === 'normal',
              'bg-white-hover border border-grey-light': contentVariant === 'secondary'
            },
            contentClassName
          )}
        >
          <div className="flex items-start flex-col">{children}</div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

export default InformationToolTip
